import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client/react';
import log from 'loglevel';

import {
  GET_SNOOZED_EMAILS,
  useUnsnoozeEmail,
} from '../../../services/snoozedEmails.service';
import { FETCH_POLICY_CACHE_FIRST } from '../../../utils/constants';

interface SnoozedEmailsQueryData {
  snoozedEmails: { snoozeId: string; messageId: string }[];
}

export const useCancelSnoozeOnRemoval = () => {
  const client = useApolloClient();
  const { unsnoozeEmail } = useUnsnoozeEmail();

  const cancelSnoozeFor = useCallback(
    (messageIds: string[]) => {
      const messageIdSet = new Set(messageIds);

      const cancel = async () => {
        const { data } = await client.query<SnoozedEmailsQueryData>({
          query: GET_SNOOZED_EMAILS,
          fetchPolicy: FETCH_POLICY_CACHE_FIRST,
        });
        const snoozesToCancel = (data?.snoozedEmails ?? []).filter(
          (snoozedEmail) => messageIdSet.has(snoozedEmail.messageId),
        );

        if (snoozesToCancel.length === 0) {
          return;
        }

        await Promise.all(
          snoozesToCancel.map((snoozedEmail) =>
            unsnoozeEmail(snoozedEmail.snoozeId),
          ),
        );
      };

      cancel().catch((error) => {
        log.error('useCancelSnoozeOnRemoval: failed to cancel snooze', {
          error,
        });
      });
    },
    [client, unsnoozeEmail],
  );

  return { cancelSnoozeFor };
};
